import {
  CreateEventRequest,
  IntervalsRequest,
  TemplatesRequest,
  SubCategoriesRequest,
  GetEventsRequest,
  deleteEventRequest,
  GetEventDetailRequest,
  updateEventRequest,
  AllLocationRequest,
  GetLocationDetailRequest,
  UpdateLocationRequest,
  AddLocationRequest,
  DeleteLocationRequest,
  SingleTemplateRequest,
  DeleteTemplateRequest,
  AddTemplateRequest,
  UpdateTemplateRequest,
  AllCategoriesRequest,
  AddCategoryRequest,
  DeleteCategoryRequest,
  GetCategoryDetailRequest,
  UpdateCategoryRequest,
  UpdateSubCategoryRequest,
  CompleteEventRequest,
} from '../api/requests';
import type { UserStore } from '~/authentication/stores/user';
import type ApiClient from '~/common/services/ApiClient';
import type { RequestPagination } from '~/common/types';

export class CalendarService {
  apiClient: ApiClient;
  userStore: UserStore;

  constructor(apiClient: ApiClient, userStore: UserStore) {
    this.apiClient = apiClient;
    this.userStore = userStore;
  }

  async intervals(pagination: RequestPagination, params: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new IntervalsRequest({
      token: accessToken,
      params: { ...pagination, ...params },
    });

    return this.apiClient.request(request);
  }

  async templates(pagination: RequestPagination, params: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new TemplatesRequest({
      token: accessToken,
      params: { ...pagination, ...params },
    });

    return this.apiClient.request(request);
  }

  async updateTemplate(id: string, templateData: any) {
    const accessToken = this.userStore.accessToken;
    const request = new UpdateTemplateRequest({
      token: accessToken,
      id,
      templateData,
    });
    return this.apiClient.request(request);
  }
  async addTemplate(template: any) {
    const accessToken = this.userStore.accessToken;
    const request = new AddTemplateRequest({
      token: accessToken,
      template,
    });
    return this.apiClient.request(request);
  }
  async createEvent(event: any) {
    const accessToken = this.userStore.accessToken;
    const request = new CreateEventRequest({
      token: accessToken,
      event,
    });
    return this.apiClient.request(request);
  }
  async deleteTemplate(templateID: string) {
    const accessToken = this.userStore.accessToken;
    const request = new DeleteTemplateRequest({
      token: accessToken,
      templateID,
    });

    return this.apiClient.request(request);
  }
  async singleTemplate(templateID: string) {
    const accessToken = this.userStore.accessToken;
    const request = new SingleTemplateRequest({
      token: accessToken,
      templateID,
    });

    return this.apiClient.request(request);
  }
  async subcategories(categoryID: string) {
    const accessToken = this.userStore.accessToken;
    const request = new SubCategoriesRequest({
      token: accessToken,
      categoryID,
    });

    return this.apiClient.request(request);
  }
  async getEvents(
    fromDate: string,
    toDate: string,
    params?: { include?: string },
  ) {
    const accessToken = this.userStore.accessToken;
    const request = new GetEventsRequest({
      token: accessToken,
      fromDate,
      toDate,
      params,
    });
    return this.apiClient.request(request);
  }
  async getEventDetails(id: string, params?: { include?: string }) {
    const accessToken = this.userStore.accessToken;
    const request = new GetEventDetailRequest({
      token: accessToken,
      id,
      params,
    });

    return this.apiClient.request(request);
  }
  async deleteEvent(id: string, updateType: string) {
    const accessToken = this.userStore.accessToken;
    const request = new deleteEventRequest({
      token: accessToken,
      id,
      updateType,
    });

    return this.apiClient.request(request);
  }

  async updateEvent(eventID: string, event: any) {
    const accessToken = this.userStore.accessToken;
    const request = new updateEventRequest({
      token: accessToken,
      eventID,
      event,
    });

    return this.apiClient.request(request);
  }

  async allCategories(
    pagination: RequestPagination,
    params: Record<string, any>,
  ) {
    const accessToken = this.userStore.accessToken;
    const request = new AllCategoriesRequest({
      token: accessToken,
      params: { ...pagination, ...params },
    });

    return this.apiClient.request(request);
  }
  async addCategory(name: string, id: string | null) {
    const accessToken = this.userStore.accessToken;
    const request = new AddCategoryRequest({
      token: accessToken,
      name,
      id,
    });
    return this.apiClient.request(request);
  }
  async updateCategory(id: string, name: string) {
    const accessToken = this.userStore.accessToken;
    const request = new UpdateCategoryRequest({
      token: accessToken,
      id,
      name,
    });
    return this.apiClient.request(request);
  }
  async updateSubCategory(id: string, name: string, parentID: string) {
    const accessToken = this.userStore.accessToken;
    const request = new UpdateSubCategoryRequest({
      token: accessToken,
      id,
      name,
      parentID,
    });
    return this.apiClient.request(request);
  }
  async deleteCategory(id: string) {
    const accessToken = this.userStore.accessToken;
    const request = new DeleteCategoryRequest({
      token: accessToken,
      id,
    });
    return this.apiClient.request(request);
  }
  async getCategoryDetails(id: string) {
    const accessToken = this.userStore.accessToken;
    const request = new GetCategoryDetailRequest({
      token: accessToken,
      id,
    });
    return this.apiClient.request(request);
  }

  async completeEvent(event: any) {
    const accessToken = this.userStore.accessToken;
    const request = new CompleteEventRequest({
      token: accessToken,
      event,
    });
    return this.apiClient.request(request);
  }

  async allLocations(pagination: RequestPagination, params: Record<string, any>) {
    const accessToken = this.userStore.accessToken;
    const request = new AllLocationRequest({
      token: accessToken,
      params: { ...pagination, ...params },
    });

    return this.apiClient.request(request);
  }
  async getLocationDetails(id: string) {
    const accessToken = this.userStore.accessToken;
    const request = new GetLocationDetailRequest({
      token: accessToken,
      id,
    });
    return this.apiClient.request(request);
  }
  async addLocation(name: string) {
    const accessToken = this.userStore.accessToken;
    const request = new AddLocationRequest({
      token: accessToken,
      name,
    });
    return this.apiClient.request(request);
  }
  async updateLocation(id: string, name: string) {
    const accessToken = this.userStore.accessToken;
    const request = new UpdateLocationRequest({
      token: accessToken,
      id,
      name,
    });
    return this.apiClient.request(request);
  }
  async deleteLocation(id: string) {
    const accessToken = this.userStore.accessToken;
    const request = new DeleteLocationRequest({
      token: accessToken,
      id,
    });
    return this.apiClient.request(request);
  }
}
